import { cn } from '../../../lib/cn';

/**
 * Vocabulary session progress bar — two display modes.
 *
 * 1. Sectioned (preferred for onboarding): pass `sections` as
 *    [{ label, units, completed, active, stage }, ...] — each section
 *    becomes a labelled row of unit-cells. Renders as monochrome shaded
 *    blocks: completed = solid dark, active-in-progress = outlined
 *    (border only, open center), untouched = light.
 *    Section widths grow proportionally to `units` so a 1-unit section
 *    like "Sorting" doesn't stretch to match a 5-unit one.
 *
 *    Optional `onSegmentClick({ stage, unitIndex })` makes past + current
 *    cells clickable; future cells stay disabled. The parent decides
 *    where the click navigates.
 *
 * 2. Legacy single-bar (still used by the review queue): pass
 *    `current` + `total` as integers; renders a single % filled bar.
 */
export default function ProgressBar({ sections, current, total, className, onSegmentClick }) {
  if (Array.isArray(sections) && sections.length > 0) {
    return <SectionedProgressBar sections={sections} className={className} onSegmentClick={onSegmentClick} />;
  }
  return <SinglePercentProgressBar current={current} total={total} className={className} />;
}

function SinglePercentProgressBar({ current, total, className }) {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div className={cn('w-full', className)}>
      <div className="h-1 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div
          className="h-full rounded-full bg-gray-400 dark:bg-gray-500 transition-all duration-300 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function SectionedProgressBar({ sections, className, onSegmentClick }) {
  return (
    <div className={cn('w-full', className)}>
      <div className="flex items-end gap-3">
        {sections.map((section, sectionIdx) => (
          <SectionCell
            key={section.label || sectionIdx}
            section={section}
            onSegmentClick={onSegmentClick}
          />
        ))}
      </div>
    </div>
  );
}

function SectionCell({ section, onSegmentClick }) {
  const { label, units, completed, active, stage } = section;
  const safeUnits = Math.max(1, units || 0);
  const safeCompleted = Math.min(Math.max(0, completed || 0), safeUnits);

  return (
    <div
      className="flex flex-col gap-1 min-w-0"
      style={{ flexGrow: safeUnits, flexBasis: 0 }}
    >
      <span
        className={cn(
          'text-[10px] uppercase tracking-wider truncate',
          active
            ? 'text-gray-700 dark:text-gray-200 font-medium'
            : 'text-gray-400 dark:text-gray-500'
        )}
      >
        {label}
      </span>
      <div className="flex gap-0.5 h-2">
        {Array.from({ length: safeUnits }).map((_, unitIdx) => {
          const isComplete = unitIdx < safeCompleted;
          const isActive = active && unitIdx === safeCompleted;
          // Past + current cells are navigable; future cells are blocked.
          // "Past" means strictly before this section's active cell, OR any
          // cell in a section that's already fully completed.
          const isNavigable = !!onSegmentClick && (isComplete || isActive);

          const baseClasses = 'flex-1 rounded-sm transition-colors duration-300';
          const stateClasses = isComplete
            ? 'bg-gray-600 dark:bg-gray-300'
            : isActive
              ? 'bg-white dark:bg-gray-950 border-2 border-gray-600 dark:border-gray-300'
              : 'bg-gray-200 dark:bg-gray-800';

          if (isNavigable) {
            return (
              <button
                key={unitIdx}
                type="button"
                onClick={() => onSegmentClick({ stage, unitIndex: unitIdx })}
                aria-label={`Go to ${label} step ${unitIdx + 1}`}
                className={cn(
                  baseClasses,
                  stateClasses,
                  'cursor-pointer hover:opacity-80 focus:outline-none focus:ring-1 focus:ring-gray-400 dark:focus:ring-gray-500'
                )}
              />
            );
          }
          return (
            <div
              key={unitIdx}
              className={cn(baseClasses, stateClasses, 'cursor-default')}
              aria-hidden="true"
            />
          );
        })}
      </div>
    </div>
  );
}
